(function (global) {
  'use strict';

  const HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  const MAX_PAGES = 4;

  function hasValidHandle(product) {
    return Boolean(product && typeof product.handle === 'string' && HANDLE_PATTERN.test(product.handle));
  }

  function isEligible(product, currentHandle) {
    return Boolean(
      hasValidHandle(product) &&
        product.handle !== currentHandle &&
        Array.isArray(product.images) &&
        product.images.length > 0 &&
        Array.isArray(product.variants) &&
        product.variants.some((variant) => variant && variant.available === true),
    );
  }

  function dedupeByHandle(products) {
    if (!Array.isArray(products)) return [];

    const seen = new Set();
    return products.filter((product) => {
      if (!hasValidHandle(product) || seen.has(product.handle)) return false;
      seen.add(product.handle);
      return true;
    });
  }

  function selectDiscovery(products, currentHandle, relatedHandles) {
    const excluded = relatedHandles instanceof Set ? relatedHandles : new Set();
    return dedupeByHandle(products).filter(
      (product) => isEligible(product, currentHandle) && !excluded.has(product.handle),
    );
  }

  function priceToCents(price) {
    if ((typeof price !== 'string' && typeof price !== 'number') || price === '') return null;
    const amount = Number(price);
    if (!Number.isFinite(amount) || amount < 0) return null;
    return Math.round(amount * 100);
  }

  function getLowestAvailablePrice(product) {
    if (!product || !Array.isArray(product.variants)) return null;

    let lowest = null;
    for (const variant of product.variants) {
      if (!variant || variant.available !== true) continue;
      const cents = priceToCents(variant.price);
      if (cents !== null && (lowest === null || cents < lowest)) lowest = cents;
    }
    return lowest;
  }

  async function loadProductPages(loadPage, pageSize = 250, maxPages = MAX_PAGES) {
    if (typeof loadPage !== 'function') throw new TypeError('loadPage must be a function');

    const boundedPageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 250;
    const requestedPages = Number.isInteger(maxPages) && maxPages > 0 ? maxPages : MAX_PAGES;
    const pageCeiling = Math.min(requestedPages, MAX_PAGES);
    const products = [];

    for (let page = 1; page <= pageCeiling; page += 1) {
      const result = await loadPage(page, boundedPageSize);
      if (!Array.isArray(result)) throw new TypeError('Product page must resolve to an array');
      products.push(...result);
      if (result.length < boundedPageSize) break;
    }

    return products;
  }

  function shuffle(items, random) {
    for (let index = items.length - 1; index > 0; index -= 1) {
      const sample = Number(random());
      const normalized = Number.isFinite(sample) ? Math.min(Math.max(sample, 0), 0.9999999999999999) : 0;
      const swapIndex = Math.floor(normalized * (index + 1));
      [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    }
    return items;
  }

  function createDeck(items, random = Math.random) {
    const source = [];
    const seenHandles = new Set();
    for (const item of Array.isArray(items) ? items : []) {
      if (hasValidHandle(item)) {
        if (seenHandles.has(item.handle)) continue;
        seenHandles.add(item.handle);
      }
      source.push(item);
    }
    return {
      items: shuffle(source, typeof random === 'function' ? random : Math.random),
      cursor: 0,
      random: typeof random === 'function' ? random : Math.random,
    };
  }

  function nextSet(deck, size) {
    if (!deck || !Array.isArray(deck.items) || deck.items.length === 0) return [];
    const setSize = Number.isInteger(size) && size > 0 ? size : 0;
    if (setSize === 0) return [];

    if (deck.cursor >= deck.items.length) {
      shuffle(deck.items, typeof deck.random === 'function' ? deck.random : Math.random);
      deck.cursor = 0;
    }

    const result = [];
    const seenHandles = new Set();
    while (deck.cursor < deck.items.length && result.length < setSize) {
      const item = deck.items[deck.cursor];
      deck.cursor += 1;
      const key = hasValidHandle(item) ? `handle:${item.handle}` : item;
      if (seenHandles.has(key)) continue;
      seenHandles.add(key);
      result.push(item);
    }
    return result;
  }

  const api = Object.freeze({
    isEligible,
    dedupeByHandle,
    selectDiscovery,
    getLowestAvailablePrice,
    loadProductPages,
    createDeck,
    nextSet,
  });
  globalThis.PKProductRecommendations = api;

  if (typeof document === 'undefined') return;

  const initializedRoots = new WeakSet();
  const rootStates = new WeakMap();

  function getPageUrl(base, page) {
    const origin = global.location && global.location.origin;
    if (!origin) throw new TypeError('Missing page origin');

    const url = new URL(base, origin);
    if (url.origin !== origin || !/\/collections\/all\/products\.json$/.test(url.pathname)) {
      throw new TypeError('Product endpoint must be same-origin collections/all/products.json');
    }
    url.searchParams.set('limit', '250');
    url.searchParams.set('page', String(page));
    return url;
  }

  async function fetchProductPage(base, page) {
    const response = await global.fetch(getPageUrl(base, page), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response || !response.ok) throw new Error('Product page request failed');
    const payload = await response.json();
    if (!payload || !Array.isArray(payload.products)) throw new TypeError('Product payload must contain an array');
    return payload.products;
  }

  function firstValidImage(product) {
    const origin = global.location && global.location.origin;
    if (!origin || !product || !Array.isArray(product.images)) return null;

    for (const image of product.images) {
      const source = typeof image === 'string' ? image : image && image.src;
      if (typeof source !== 'string' || source.length === 0) continue;
      try {
        const url = new URL(source, origin);
        const pageProtocol = new URL(origin).protocol;
        if (url.protocol === 'https:' || (pageProtocol === 'http:' && url.protocol === 'http:')) return url.href;
      } catch (_error) {
        // Malformed remote image records are discarded.
      }
    }
    return null;
  }

  function productUrl(handle) {
    const origin = global.location && global.location.origin;
    if (!origin || !HANDLE_PATTERN.test(handle)) return null;
    const url = new URL(`/products/${encodeURIComponent(handle)}`, origin);
    if (url.origin !== origin || !url.pathname.startsWith('/products/')) return null;
    return url.href;
  }

  function formatPrice(cents, moneyFormat) {
    const shopify = global.Shopify;
    if (shopify && typeof shopify.formatMoney === 'function') {
      try {
        return shopify.formatMoney(cents, moneyFormat);
      } catch (_error) {
        // Fall through to the platform currency formatter.
      }
    }

    const currency = (shopify && shopify.currency && shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
    }
  }

  function hasVaryingAvailablePrices(product) {
    const prices = new Set();
    for (const variant of product.variants) {
      if (!variant || variant.available !== true) continue;
      const cents = priceToCents(variant.price);
      if (cents !== null) prices.add(cents);
    }
    return prices.size > 1;
  }

  function createCard(product, moneyFormat) {
    const href = productUrl(product.handle);
    const imageSource = firstValidImage(product);
    const cents = getLowestAvailablePrice(product);
    if (!href || !imageSource || cents === null) return null;

    const item = document.createElement('li');
    item.className = 'pk-product-recs__item';
    item.setAttribute('data-pk-product-handle', product.handle);
    item.setAttribute('data-pk-rec-source', 'discovery');

    const link = document.createElement('a');
    link.className = 'pk-product-recs__card';
    link.href = href;

    const imageWrap = document.createElement('span');
    imageWrap.className = 'pk-product-recs__image-wrap';
    const image = document.createElement('img');
    image.className = 'pk-product-recs__image';
    image.loading = 'lazy';
    image.src = imageSource;
    image.alt = typeof product.title === 'string' ? product.title : '';
    imageWrap.appendChild(image);
    if (cents > 100000) {
      const seal = document.createElement('span');
      seal.className = 'pk-collector-seal pk-collector-seal--compact notranslate';
      seal.setAttribute('aria-label', "Collector's piece");
      seal.setAttribute('translate', 'no');
      const top = document.createElement('span');
      top.className = 'pk-collector-seal__word pk-collector-seal__word--top';
      top.setAttribute('aria-hidden', 'true');
      top.textContent = 'COLLECTOR';
      const monogram = document.createElement('span');
      monogram.className = 'pk-collector-seal__monogram';
      monogram.setAttribute('aria-hidden', 'true');
      monogram.textContent = 'PK';
      const bottom = document.createElement('span');
      bottom.className = 'pk-collector-seal__word pk-collector-seal__word--bottom';
      bottom.setAttribute('aria-hidden', 'true');
      bottom.textContent = 'PIECE';
      seal.append(top, monogram, bottom);
      imageWrap.appendChild(seal);
    }

    const copy = document.createElement('span');
    copy.className = 'pk-product-recs__copy';
    const title = document.createElement('span');
    title.className = 'pk-product-recs__product-title';
    title.textContent = typeof product.title === 'string' ? product.title : '';
    const price = document.createElement('span');
    price.className = 'pk-product-recs__price';
    const prefix = hasVaryingAvailablePrices(product) ? 'From ' : '';
    if (global.PKOpeningSale) global.PKOpeningSale.renderPrice(price, product, cents, amount => formatPrice(amount, moneyFormat), prefix);
    else price.textContent = `${prefix}${formatPrice(cents, moneyFormat)}`;

    copy.append(title, price);
    link.append(imageWrap, copy);
    item.appendChild(link);
    return item;
  }

  function canRenderProduct(product) {
    return Boolean(
      product &&
        typeof product.title === 'string' &&
        product.title.length > 0 &&
        productUrl(product.handle) &&
        firstValidImage(product) &&
        getLowestAvailablePrice(product) !== null,
    );
  }

  function restoreDeck(deck, snapshot) {
    deck.items = snapshot.items;
    deck.cursor = snapshot.cursor;
  }

  function commitNext(root, row, rail, button, state, moneyFormat) {
    if (state.busy || !root.isConnected) return false;

    state.busy = true;
    state.renderVersion += 1;
    const version = state.renderVersion;
    const snapshot = { items: state.deck.items.slice(), cursor: state.deck.cursor };
    button.disabled = true;
    root.classList.add('pk-product-recs--changing');
    root.setAttribute('data-pk-rec-state', 'changing');

    try {
      const products = nextSet(state.deck, 10);
      const fragment = document.createDocumentFragment();
      for (const product of products) {
        const card = createCard(product, moneyFormat);
        if (card) fragment.appendChild(card);
      }

      if (!fragment.childNodes.length || !root.isConnected || state.renderVersion !== version) {
        restoreDeck(state.deck, snapshot);
        return false;
      }

      rail.replaceChildren(fragment);
      row.hidden = false;
      root.classList.add('pk-product-recs--ready');
      return true;
    } catch (_error) {
      restoreDeck(state.deck, snapshot);
      return false;
    } finally {
      if (state.renderVersion === version) {
        root.classList.remove('pk-product-recs--changing');
        root.removeAttribute('data-pk-rec-state');
        button.disabled = false;
        state.busy = false;
      }
    }
  }

  async function initializeRoot(root) {
    if (initializedRoots.has(root) || root.getAttribute('data-pk-rec-initialized') === 'true') return;
    initializedRoots.add(root);
    root.setAttribute('data-pk-rec-initialized', 'true');

    const row = root.querySelector('[data-pk-rec-row="discovery"]');
    if (row) row.hidden = true;

    try {
      const configElement = root.querySelector('[data-pk-rec-config]');
      const relatedRail = root.querySelector('[data-pk-rec-related-rail]');
      const rail = root.querySelector('[data-pk-rec-discovery-rail]');
      const button = root.querySelector('[data-pk-rec-shuffle]');
      if (!configElement || !relatedRail || !row || !rail || !button) return;

      const config = JSON.parse(configElement.textContent || '');
      if (!config || typeof config.currentHandle !== 'string' || typeof config.allProductsUrl !== 'string') return;
      getPageUrl(config.allProductsUrl, 1);

      const relatedHandles = new Set();
      for (const card of relatedRail.querySelectorAll('[data-pk-product-handle]')) {
        const handle = card.getAttribute('data-pk-product-handle');
        if (typeof handle === 'string' && HANDLE_PATTERN.test(handle)) relatedHandles.add(handle);
      }

      const products = await loadProductPages(
        (page) => fetchProductPage(config.allProductsUrl, page),
        250,
        MAX_PAGES,
      );
      const eligibleProducts = selectDiscovery(products, config.currentHandle, relatedHandles).filter(canRenderProduct);
      if (eligibleProducts.length === 0 || !root.isConnected) return;

      const state = { deck: createDeck(eligibleProducts), renderVersion: 0, busy: false };
      rootStates.set(root, state);
      if (!commitNext(root, row, rail, button, state, config.moneyFormat)) return;

      button.addEventListener('click', () => {
        commitNext(root, row, rail, button, state, config.moneyFormat);
      });
    } catch (_error) {
      if (row) row.hidden = true;
    }
  }

  function boot() {
    for (const root of document.querySelectorAll('[data-pk-product-recommendations]')) {
      initializeRoot(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(globalThis);
